import { defineStore } from 'pinia';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '@/firebase';

export const useSupplyStore = defineStore('supply', {
  state: () => ({
    supplies: {},
    openContainers: {},
    unsubs: [],
  }),
  persist: { paths: ['supplies', 'openContainers'] },
  getters: {
    allSupplies(state) {
      return Object.values(state.supplies).sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
    },
    activeSupplies(state) {
      return Object.values(state.supplies)
        .filter((s) => !s.archived)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    archivedSupplies(state) {
      return Object.values(state.supplies)
        .filter((s) => !!s.archived)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    quantitySupplies() {
      return this.allSupplies.filter((s) => s.tracking === 'quantity');
    },
    containerSupplies() {
      return this.allSupplies.filter((s) => s.tracking === 'container');
    },
    activeOpenContainers(state) {
      return Object.values(state.openContainers).filter((c) => c.status === 'active');
    },
    openCountBySupplyId(state) {
      const counts = {};
      for (const container of Object.values(state.openContainers)) {
        if (container.status !== 'active' || !container.supplyId) continue;
        counts[container.supplyId] = (counts[container.supplyId] || 0) + 1;
      }
      return counts;
    },
    supplyById: (state) => (id) => state.supplies[id] || null,
  },
  actions: {
    upsertSupply({ id, value }) {
      this.supplies = { ...this.supplies, [id]: value };
    },
    removeSupply(id) {
      const supplies = { ...this.supplies };
      delete supplies[id];
      this.supplies = supplies;
    },
    upsertOpenContainer({ id, value }) {
      this.openContainers = { ...this.openContainers, [id]: value };
    },
    removeOpenContainer(id) {
      const openContainers = { ...this.openContainers };
      delete openContainers[id];
      this.openContainers = openContainers;
    },
    clearUnsubs() {
      for (const unsub of this.unsubs) {
        if (typeof unsub === 'function') unsub();
      }
      this.unsubs = [];
    },
    clear() {
      this.clearUnsubs();
      this.supplies = {};
      this.openContainers = {};
    },
    registerSnapshot() {
      this.clearUnsubs();
      const suppliesUnsub = onSnapshot(
        collection(firestore, 'supplies'),
        (snapshot) => {
          snapshot.docChanges().forEach((element) => {
            const elementData = { ...element.doc.data() };
            elementData.id = element.doc.id;
            if (element.type === 'added' || element.type === 'modified') {
              this.upsertSupply({ id: element.doc.id, value: elementData });
            } else {
              this.removeSupply(element.doc.id);
            }
          });
        },
        (error) => {
          console.error('Supply - Error registering supplies snapshot', error);
        }
      );
      const openQuery = query(collection(firestore, 'openContainers'), where('status', '==', 'active'));
      const openUnsub = onSnapshot(
        openQuery,
        (snapshot) => {
          const activeIds = new Set();
          snapshot.docChanges().forEach((element) => {
            const elementData = { ...element.doc.data() };
            elementData.id = element.doc.id;
            if (element.type === 'added' || element.type === 'modified') {
              activeIds.add(element.doc.id);
              this.upsertOpenContainer({ id: element.doc.id, value: elementData });
            } else {
              this.removeOpenContainer(element.doc.id);
            }
          });
          snapshot.docs.forEach((d) => activeIds.add(d.id));
          for (const id of Object.keys(this.openContainers)) {
            if (!activeIds.has(id)) {
              this.removeOpenContainer(id);
            }
          }
        },
        (error) => {
          console.error('Supply - Error registering openContainers snapshot', error);
        }
      );
      this.unsubs.push(suppliesUnsub, openUnsub);
    },
  },
});
